import type { ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faArrowUpRightFromSquare,
  faBolt,
  faBoxesStacked,
  faBug,
  faCircleInfo,
  faCode,
  faFileLines,
  faFileShield,
  faLink,
  faLocationCrosshairs,
  faScrewdriverWrench,
  faShieldCheck,
  faTag,
  faTriangleExclamation,
  faUserSecret,
  faVirus,
} from '@fortawesome/pro-regular-svg-icons';
import {
  Anchor,
  Badge,
  Box,
  Group,
  Paper,
  Progress,
  SimpleGrid,
  Stack,
  Text,
  Timeline,
} from '@cowbell/ui';
import type {
  CveAttackerKb,
  CveCwe,
  CveExploit,
  CveProfile,
  CveZdiAdvisory,
} from '../../types';
import {
  formatAttackVector,
  formatCveDate,
  formatEpss,
  formatReferenceLabel,
  ordinalSuffix,
  parseCweMitigations,
  parseDisclosureTimeline,
  referenceDomain,
} from './cve-format';

function exploitTitle(exploit: CveExploit): string {
  if (exploit.title) return exploit.title;
  if (exploit.edbId != null) return `Exploit ${exploit.edbId}`;
  return 'Exploit';
}

/** Renders a CWE from either the enriched {id,name} shape or the pre-migration
 * plain-string shape, so the panel is correct regardless of backend rollout
 * order (an old API still returns cwes as string[]). */
function formatCwe(cwe: CveCwe | string): string {
  if (typeof cwe === 'string') return cwe;
  return cwe.name ? `${cwe.id} · ${cwe.name}` : cwe.id;
}

/** Normalizes both accepted CWE shapes to the object form so the Weakness
 * section can render prose without re-narrowing at every use. */
function toCweObject(cwe: CveCwe | string): CveCwe {
  return typeof cwe === 'string' ? { id: cwe } : cwe;
}

/** True when AttackerKB's block would only restate what the panel already
 * shows. Its `title` is frequently just the CVE id and its `description` the
 * same NVD text as the summary, which rendered the paragraph twice. */
function isRedundant(text: string | undefined, ...against: (string | undefined)[]): boolean {
  if (!text) return true;
  const normalized = text.trim().toLowerCase();
  return against.some((other) => (other ?? '').trim().toLowerCase() === normalized);
}

/** True when the AttackerKB block has at least one field we actually render,
 * so the section is skipped for a block carrying only url/other unrendered data
 * (keeps the sparse-CVE data-gating intact). */
function attackerkbHasContent(akb: CveAttackerKb, cveId: string, cveDescription?: string): boolean {
  // A title that is just the CVE id and a description identical to the summary
  // are suppressed at render, so neither counts as content here — otherwise the
  // section renders as an empty card.
  return !!(
    akb.privileges ||
    akb.userInteraction ||
    akb.disclosureDate ||
    !isRedundant(akb.title, cveId) ||
    !isRedundant(akb.description, cveDescription)
  );
}

/** True when a ZDI advisory has at least one field we actually render, so a
 * content-less advisory doesn't produce an empty headed section. */
function zdiHasContent(advisory: CveZdiAdvisory): boolean {
  return !!(
    advisory.zdiId ||
    advisory.title ||
    advisory.credit ||
    advisory.cvss != null ||
    advisory.publishedDate ||
    advisory.disclosureTimeline ||
    advisory.vulnerabilityDetails ||
    advisory.additionalDetails
  );
}

interface MetaRow {
  readonly label: string;
  readonly value: ReactNode;
}

interface SectionHeaderProps {
  readonly icon: IconDefinition;
  readonly children: ReactNode;
}

function SectionHeader({ icon, children }: Readonly<SectionHeaderProps>) {
  // The label carries full text contrast — a dimmed heading over dimmed body
  // copy left the whole panel reading as secondary content. Only the icon
  // stays muted, so the sections still scan as chrome rather than data.
  return (
    <Group gap={8} mb="xs" wrap="nowrap">
      <FontAwesomeIcon icon={icon} opacity={0.6} />
      <Text size="sm" fw={600}>
        {children}
      </Text>
    </Group>
  );
}

interface StatTileProps {
  readonly label: string;
  readonly value: ReactNode;
  readonly suffix?: string;
  readonly sub?: string;
  readonly meter?: number;
  readonly meterColor?: string;
}

function StatTile({
  label,
  value,
  suffix,
  sub,
  meter,
  meterColor,
}: Readonly<StatTileProps>) {
  return (
    <Paper radius="md" p="sm" bg="var(--mantine-color-default-hover)">
      <Text size="sm" c="dimmed">
        {label}
      </Text>
      <Group gap={4} align="baseline">
        <Text fw={600} style={{ fontSize: 26, lineHeight: 1.15 }}>
          {value}
        </Text>
        {suffix && (
          <Text c="dimmed" style={{ fontSize: 14 }}>
            {suffix}
          </Text>
        )}
      </Group>
      {meter != null && (
        <Progress
          value={meter}
          color={meterColor ?? 'gray'}
          size="xs"
          radius="xl"
          mt={6}
        />
      )}
      {sub && (
        <Text size="sm" c="dimmed" mt={4}>
          {sub}
        </Text>
      )}
    </Paper>
  );
}

interface RiskChipProps {
  readonly color: string;
  readonly icon: IconDefinition;
  readonly children: ReactNode;
}

function RiskChip({ color, icon, children }: Readonly<RiskChipProps>) {
  return (
    <Badge
      color={color}
      variant="light"
      radius="sm"
      leftSection={<FontAwesomeIcon icon={icon} />}
      styles={{
        root: { textTransform: 'none' },
        label: { textTransform: 'none', fontWeight: 400 },
      }}
    >
      {children}
    </Badge>
  );
}

interface AttrChipProps {
  readonly label: string;
  readonly value: string;
}

function AttrChip({ label, value }: Readonly<AttrChipProps>) {
  return (
    <Badge
      variant="default"
      radius="sm"
      styles={{
        root: { textTransform: 'none' },
        label: { textTransform: 'none', fontWeight: 400 },
      }}
    >
      {label}{' '}
      <Text span inherit fw={500}>
        {value}
      </Text>
    </Badge>
  );
}

interface MetaListProps {
  readonly items: ReadonlyArray<MetaRow>;
}

function MetaList({ items }: Readonly<MetaListProps>) {
  const rows = items.filter((i) => i.value != null && i.value !== '');
  if (rows.length === 0) return null;
  return (
    <Stack gap={4}>
      {rows.map((row) => (
        <Group key={row.label} justify="space-between" gap="sm" wrap="nowrap">
          <Text size="sm" c="dimmed">
            {row.label}
          </Text>
          <Text size="sm" fw={500} ta="right">
            {row.value}
          </Text>
        </Group>
      ))}
    </Stack>
  );
}

interface ReferenceLinkProps {
  readonly url: string;
  readonly label: string;
}

function ReferenceLink({ url, label }: Readonly<ReferenceLinkProps>) {
  return (
    <Anchor href={url} target="_blank" rel="noopener noreferrer" size="sm">
      <FontAwesomeIcon icon={faArrowUpRightFromSquare} /> {label}
    </Anchor>
  );
}

interface CardProps {
  readonly children: ReactNode;
}

function InfoCard({ children }: Readonly<CardProps>) {
  return (
    <Paper withBorder radius="md" p="md">
      <Stack gap="xs">{children}</Stack>
    </Paper>
  );
}

interface CveDetailBodyProps {
  readonly profile: CveProfile;
}

export default function CveDetailBody({
  profile,
}: Readonly<CveDetailBodyProps>) {
  const cwes = (profile.cwes ?? []).map(toCweObject);
  const affectedProducts = profile.affectedProducts ?? [];
  const references = profile.references ?? [];
  const zdiAdvisories = (profile.zdiAdvisories ?? []).filter(zdiHasContent);
  const exploits = profile.exploits ?? [];
  // AttackerKB restates the CVE id as its title and the NVD text as its
  // description on a large share of records — show each only when it adds
  // something the Summary section above doesn't already say.
  const akbTitle = profile.attackerkb?.title;
  const akbDescription = profile.attackerkb?.description;
  const showAkbTitle = !isRedundant(akbTitle, profile.cveId);
  const showAkbDescription = !isRedundant(akbDescription, profile.description);
  const hasMetadata =
    !!profile.publishedAt || !!profile.updatedAt || !!profile.status;
  const vendorLinks = (profile.vendorAdvisoryLinks ?? []).filter(
    (l): l is string => !!l && l.trim().length > 0
  );
  const hasRemediation =
    vendorLinks.length > 0 ||
    !!profile.kev?.requiredAction ||
    !!profile.kev?.dueDate ||
    !!profile.kev?.dateAdded;

  let epssPercentileLabel: string | null = null;
  if (profile.epssPercentile != null) {
    const pct = Math.round(profile.epssPercentile * 100);
    epssPercentileLabel = `${pct}${ordinalSuffix(pct)} percentile`;
  }

  const tiles: ReactNode[] = [];
  if (profile.epss != null) {
    tiles.push(
      <StatTile
        key="epss"
        label="EPSS"
        value={formatEpss(profile.epss)}
        meter={Math.round(profile.epss * 100)}
        meterColor="orange"
        sub={epssPercentileLabel ?? undefined}
      />
    );
  }
  if (profile.cvss != null) {
    tiles.push(
      <StatTile
        key="cvss"
        label="CVSS"
        value={profile.cvss.toFixed(1)}
        suffix="/10"
        sub="Derived from ZDI"
      />
    );
  }
  if (profile.attackerkbRisk != null) {
    tiles.push(
      <StatTile
        key="akb"
        label="AttackerKB"
        value={profile.attackerkbRisk}
        suffix="/5"
        sub="Community score"
      />
    );
  }

  const chips: ReactNode[] = [];
  if (profile.kev?.listed) {
    chips.push(
      <RiskChip key="kev" color="red" icon={faShieldCheck}>
        CISA KEV listed
      </RiskChip>
    );
  }
  // CISA KEV's knownRansomwareCampaignUse is "Known" | "Unknown"; only a
  // confirmed "Known" is an active-threat signal worth a red urgency chip —
  // "Unknown" (the common default) must not read as elevated.
  if (profile.kev?.ransomwareCampaignUse?.toLowerCase() === 'known') {
    chips.push(
      <RiskChip key="ransomware" color="red" icon={faVirus}>
        Ransomware: {profile.kev.ransomwareCampaignUse}
      </RiskChip>
    );
  }
  if (profile.zerodayReported) {
    chips.push(
      <RiskChip key="zeroday" color="orange" icon={faBolt}>
        Reported zero-day
      </RiskChip>
    );
  }
  const exploitCount = profile.exploitCount ?? 0;
  if (exploitCount > 0) {
    chips.push(
      <RiskChip key="exploits" color="gray" icon={faCode}>
        {exploitCount} exploit{exploitCount === 1 ? '' : 's'} ·{' '}
        {profile.verifiedExploitCount ?? 0} verified
      </RiskChip>
    );
  }
  if (profile.attackVector) {
    chips.push(
      <RiskChip key="vector" color="gray" icon={faLocationCrosshairs}>
        {formatAttackVector(profile.attackVector)} vector
      </RiskChip>
    );
  }

  return (
    <Stack gap="lg">
      {tiles.length > 0 && (
        <SimpleGrid cols={Math.min(tiles.length, 3)} spacing="xs">
          {tiles}
        </SimpleGrid>
      )}

      {chips.length > 0 && <Group gap="xs">{chips}</Group>}

      {profile.description && (
        <Box>
          <SectionHeader icon={faFileLines}>Summary</SectionHeader>
          <Text size="md">{profile.description}</Text>
        </Box>
      )}

      {exploits.length > 0 && (
        <Box>
          <SectionHeader icon={faBug}>Exploits</SectionHeader>
          <Stack gap="xs">
            {exploits.map((exploit, index) => {
              const key = exploit.url ?? exploit.edbId ?? index;
              const title = exploitTitle(exploit);
              const platform = [exploit.type, exploit.platform]
                .filter(Boolean)
                .join(' · ');
              return (
                <InfoCard key={key}>
                  {exploit.url ? (
                    <Anchor
                      href={exploit.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      fw={500}
                    >
                      {title}
                    </Anchor>
                  ) : (
                    <Text fw={500}>{title}</Text>
                  )}
                  <MetaList
                    items={[
                      {
                        label: 'Source',
                        value: exploit.url
                          ? referenceDomain(exploit.url)
                          : undefined,
                      },
                      { label: 'Author', value: exploit.author },
                      {
                        label: 'Published',
                        value: exploit.date
                          ? formatCveDate(exploit.date)
                          : undefined,
                      },
                      {
                        label: 'Type · platform',
                        value: platform || undefined,
                      },
                    ]}
                  />
                  {exploit.verified && (
                    <Badge
                      color="green"
                      variant="light"
                      radius="sm"
                      styles={{ label: { textTransform: 'none' } }}
                    >
                      Verified
                    </Badge>
                  )}
                </InfoCard>
              );
            })}
          </Stack>
        </Box>
      )}

      {zdiAdvisories.length > 0 && (
        <Box>
          <SectionHeader icon={faFileShield}>ZDI advisory</SectionHeader>
          <Stack gap="xs">
            {zdiAdvisories.map((advisory, index) => {
              const key = advisory.zdiId ?? index;
              const heading = [advisory.zdiId, advisory.title]
                .filter(Boolean)
                .join(' — ');
              const timeline = parseDisclosureTimeline(
                advisory.disclosureTimeline
              );
              const showTimeline =
                timeline.length > 1 || timeline.some((e) => e.date);
              return (
                <InfoCard key={key}>
                  {heading && <Text fw={500}>{heading}</Text>}
                  <MetaList
                    items={[
                      { label: 'Credit', value: advisory.credit },
                      {
                        label: 'CVSS',
                        value:
                          advisory.cvss != null
                            ? advisory.cvss.toFixed(1)
                            : undefined,
                      },
                      {
                        label: 'Published',
                        value: advisory.publishedDate
                          ? formatCveDate(advisory.publishedDate)
                          : undefined,
                      },
                    ]}
                  />
                  {showTimeline && (
                    <Timeline
                      active={timeline.length - 1}
                      bulletSize={10}
                      lineWidth={1}
                      mt={4}
                    >
                      {timeline.map((entry, i) => (
                        <Timeline.Item
                          key={`${entry.date}-${i}`}
                          title={
                            entry.date ? (
                              <Text size="xs" fw={500}>
                                {entry.date}
                              </Text>
                            ) : undefined
                          }
                        >
                          <Text size="sm" c="dimmed">
                            {entry.label}
                          </Text>
                        </Timeline.Item>
                      ))}
                    </Timeline>
                  )}
                  {!showTimeline && timeline.length > 0 && (
                    <Text size="sm">
                      {timeline.map((entry) => entry.label).join(' ')}
                    </Text>
                  )}
                  {advisory.vulnerabilityDetails && (
                    <Text size="md">{advisory.vulnerabilityDetails}</Text>
                  )}
                  {advisory.additionalDetails && (
                    <Text size="md">{advisory.additionalDetails}</Text>
                  )}
                </InfoCard>
              );
            })}
          </Stack>
        </Box>
      )}

      {profile.attackerkb &&
        attackerkbHasContent(
          profile.attackerkb,
          profile.cveId,
          profile.description
        ) && (
        <Box>
          <SectionHeader icon={faUserSecret}>
            Attacker perspective
          </SectionHeader>
          <InfoCard>
            {(profile.attackerkb.privileges ||
              profile.attackerkb.userInteraction ||
              profile.attackerkb.disclosureDate) && (
              <Group gap={6}>
                {profile.attackerkb.privileges && (
                  <AttrChip
                    label="Privileges"
                    value={profile.attackerkb.privileges}
                  />
                )}
                {profile.attackerkb.userInteraction && (
                  <AttrChip
                    label="User interaction"
                    value={profile.attackerkb.userInteraction}
                  />
                )}
                {profile.attackerkb.disclosureDate && (
                  <AttrChip
                    label="Disclosed"
                    value={formatCveDate(profile.attackerkb.disclosureDate)}
                  />
                )}
              </Group>
            )}
            {showAkbTitle && <Text fw={600}>{akbTitle}</Text>}
            {showAkbDescription && <Text size="md">{akbDescription}</Text>}
          </InfoCard>
        </Box>
      )}

      {cwes.length > 0 && (
        <Box>
          <SectionHeader icon={faTriangleExclamation}>Weakness</SectionHeader>
          <Stack gap="xs">
            {cwes.map((cwe) => {
              const mitigations = parseCweMitigations(cwe.potentialMitigations);
              return (
                <InfoCard key={cwe.id}>
                  <Group gap={6}>
                    <RiskChip color="gray" icon={faTag}>
                      {formatCwe(cwe)}
                    </RiskChip>
                  </Group>
                  {cwe.description && <Text size="md">{cwe.description}</Text>}
                  {mitigations.length > 0 && (
                    <Stack gap={6} mt={4}>
                      <Text size="sm" fw={600} c="dimmed">
                        How to mitigate
                      </Text>
                      {mitigations.map((mitigation) => {
                        const scope = [mitigation.phase, mitigation.strategy]
                          .filter(Boolean)
                          .join(' · ');
                        return (
                          <Box key={`${cwe.id}-${mitigation.label}`}>
                            {scope && (
                              <Text size="sm" fw={500} c="dimmed">
                                {scope}
                              </Text>
                            )}
                            <Text size="sm">{mitigation.label}</Text>
                          </Box>
                        );
                      })}
                    </Stack>
                  )}
                </InfoCard>
              );
            })}
          </Stack>
        </Box>
      )}

      {affectedProducts.length > 0 && (
        <Box>
          <SectionHeader icon={faBoxesStacked}>Affected products</SectionHeader>
          <Group gap={6}>
            {affectedProducts.map((product) => (
              <RiskChip key={product} color="gray" icon={faBoxesStacked}>
                {product}
              </RiskChip>
            ))}
          </Group>
        </Box>
      )}

      {references.length > 0 && (
        <Box>
          <SectionHeader icon={faLink}>References</SectionHeader>
          <Stack gap={6}>
            {references.map((ref) => (
              <ReferenceLink
                key={ref.url}
                url={ref.url}
                label={formatReferenceLabel(ref)}
              />
            ))}
          </Stack>
        </Box>
      )}

      {hasRemediation && (
        <Box>
          <SectionHeader icon={faScrewdriverWrench}>Remediation</SectionHeader>
          <InfoCard>
            {vendorLinks.map((url) => (
              <ReferenceLink
                key={url}
                url={url}
                label={formatReferenceLabel({ url })}
              />
            ))}
            {profile.kev?.requiredAction && (
              <Text size="md">{profile.kev.requiredAction}</Text>
            )}
            <MetaList
              items={[
                {
                  label: 'Federal remediation due',
                  value: profile.kev?.dueDate
                    ? formatCveDate(profile.kev.dueDate)
                    : undefined,
                },
                {
                  label: 'Added to CISA KEV',
                  value: profile.kev?.dateAdded
                    ? formatCveDate(profile.kev.dateAdded)
                    : undefined,
                },
              ]}
            />
          </InfoCard>
        </Box>
      )}

      {hasMetadata && (
        <Box>
          <SectionHeader icon={faCircleInfo}>Metadata</SectionHeader>
          <MetaList
            items={[
              {
                label: 'Published',
                value: profile.publishedAt
                  ? formatCveDate(profile.publishedAt)
                  : undefined,
              },
              {
                label: 'Updated',
                value: profile.updatedAt
                  ? formatCveDate(profile.updatedAt)
                  : undefined,
              },
              { label: 'Status', value: profile.status },
            ]}
          />
        </Box>
      )}
    </Stack>
  );
}
